import React, { useState } from 'react';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameDay, isToday, addMonths, subMonths, isBefore, startOfDay } from 'date-fns';
import { id } from 'date-fns/locale';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

interface CalendarPickerProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
}

export const CalendarPicker = ({ selectedDate, onDateSelect }: CalendarPickerProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const daysLabels = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const rows: any[] = [];
  let daysInRow: any[] = [];
  let day = startDate;

  while (day <= endDate) {
    for (let i = 0; i < 7; i++) {
      const cloneDay = day;
      const isPast = isBefore(cloneDay, startOfDay(new Date()));
      const isSelected = isSameDay(cloneDay, selectedDate);
      const isCurrentMonth = isSameDay(startOfMonth(cloneDay), monthStart);

      daysInRow.push(
        <button
          key={day.toString()}
          disabled={isPast}
          onClick={() => onDateSelect(cloneDay)}
          className={cn(
            "h-12 w-full flex items-center justify-center rounded-2xl text-sm font-bold transition-all relative group",
            isPast ? "text-slate-200 cursor-not-allowed" : 
            isSelected ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/40" : 
            isCurrentMonth ? "text-slate-900 hover:bg-slate-50" : "text-slate-300",
          )}
        >
          {format(day, "d")}
          {isToday(cloneDay) && !isSelected && (
            <div className="absolute bottom-2 w-1 h-1 bg-emerald-500 rounded-full" />
          )}
        </button>
      );
      day = addDays(day, 1);
    }
    rows.push(
      <div className="grid grid-cols-7 gap-1" key={day.toString()}>
        {daysInRow}
      </div>
    );
    daysInRow = [];
  }

  return (
    <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-xl shadow-slate-200/40">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-xl font-bold text-slate-900 tracking-tight">
          {format(currentMonth, "MMMM yyyy")}
        </h3>
        <div className="flex gap-2">
          <button 
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-3 hover:bg-slate-50 rounded-xl border border-slate-100 transition-colors"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
          </button>
          <button 
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-3 hover:bg-slate-50 rounded-xl border border-slate-100 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-4">
        {daysLabels.map(d => (
          <div key={d} className="text-center text-[10px] uppercase font-bold text-slate-400 tracking-widest py-2">
            {d}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-1">
        {rows}
      </div>
    </div>
  );
};
